import { TContent, TContentDetailsPayload, TContentType } from '@/types/Content.types';
import { TArticleSectionDraft, TContentFormValues, TPendingImageUpload } from '@/modules/content/creation/model/types';

export const buildContentDetails = (type: TContentType, values: TContentFormValues): TContentDetailsPayload => {
  if (type === 'post') {
    // A post is a single block of text, authored as the description on the details
    // step so it can be seen in the thumbnail preview. The backend still requires a
    // body for posts, so send the same text.
    return { body: values.description.trim() };
  }

  if (type === 'video') {
    // Source-specific details (mux/youtube) are set via their dedicated endpoints;
    // the create payload only carries the full description.
    return { fullDescription: values.fullDescription || undefined };
  }

  if (type === 'article') {
    return {
      sections: values.sections.map((section, sectionIndex) => ({
        _id: section._id,
        title: section.title || undefined,
        text: section.text,
        imageUrl: section.imageUrl ?? undefined,
        order: sectionIndex,
        subsections: section.subsections.map((sub, subIndex) => ({
          _id: sub._id,
          title: sub.title || undefined,
          text: sub.text,
          imageUrl: sub.imageUrl ?? undefined,
          order: subIndex,
        })),
      })),
    };
  }

  return {};
};

// `details` is typed as an opaque object in the generated schema; the per-type shape
// is documented in the backend content DTO. We narrow with a local cast.
type RawSubsection = { _id?: string; title?: string; text?: string; imageUrl?: string | null; order?: number };
type RawSection = RawSubsection & { subsections?: RawSubsection[] };
type RawDetails = {
  body?: string;
  sections?: RawSection[];
  fullDescription?: string;
  source?: string;
  mux?: { status?: string | null; playbackId?: string | null };
  youtube?: { videoId?: string };
};

// Maps a persisted content row back into the editor form model (edit mode).
export const mapContentToFormValues = (content: TContent): Partial<TContentFormValues> => {
  const details = (content.details ?? {}) as RawDetails;
  const videoSource = details.source === 'youtube' ? 'youtube' : 'mux';
  const youtubeUrl = details.youtube?.videoId ? `https://youtu.be/${details.youtube.videoId}` : '';
  const hasOriginalRemoteVideo =
    videoSource === 'youtube'
      ? Boolean(details.youtube?.videoId)
      : Boolean(details.mux?.playbackId || (details.mux?.status && details.mux.status !== 'errored'));

  const sections: TArticleSectionDraft[] = (details.sections ?? []).map((section) => ({
    _id: section._id ?? crypto.randomUUID(),
    title: section.title ?? '',
    text: section.text ?? '',
    imageUrl: section.imageUrl ?? null,
    imageFile: null,
    subsections: (section.subsections ?? []).map((sub) => ({
      _id: sub._id ?? crypto.randomUUID(),
      title: sub.title ?? '',
      text: sub.text ?? '',
      imageUrl: sub.imageUrl ?? null,
      imageFile: null,
    })),
  }));

  return {
    title: content.title ?? '',
    description: content.description ?? '',
    domains: content.domains ?? [],
    subdomains: content.subdomains ?? [],
    isPublic: content.visibility?.public ?? false,
    body: details.body ?? '',
    sections,
    videoSource,
    youtubeUrl,
    fullDescription: details.fullDescription ?? '',
    muxStatus: details.mux?.status ?? null,
    muxPlaybackId: details.mux?.playbackId ?? null,
    originalVideoSource: videoSource,
    originalYoutubeUrl: youtubeUrl,
    hasOriginalRemoteVideo,
  };
};

// All sections + subsections with a newly-picked file to upload after save.
export const collectImageUploads = (values: TContentFormValues): TPendingImageUpload[] => {
  const uploads: TPendingImageUpload[] = [];
  for (const section of values.sections) {
    if (section.imageFile) {
      uploads.push({ sectionId: section._id, file: section.imageFile });
    }
    for (const sub of section.subsections) {
      if (sub.imageFile) {
        uploads.push({ sectionId: section._id, subsectionId: sub._id, file: sub.imageFile });
      }
    }
  }
  return uploads;
};
